import { api } from '../_client';
import type { ChildrenListResponse } from '../../../types/api';

/**
 * The children listing — the file browser's ONE navigation contract (STD-029
 * R-8, arc B-4).
 *
 * It replaces `browseFiles`, which returned a flat, recursive, path-keyed
 * projection of the caller's whole tree and left the browser to synthesise
 * folders from key segments. That endpoint still exists and is permanent, but it
 * belongs to MissionPlanner now (D-4b): a path is a coordinate inside one
 * organization's own tree, so it could never address a folder somebody else
 * shared — which is the whole of D-6.
 *
 * ⚠ **R-3 applies to every declaration in this file.** Three client types that
 * omitted `object_id` are the #1324 root-cause chain: the field rode through the
 * runtime all along, and every reader — human and compiler — concluded there was
 * nothing to carry, so the one place that built a NEW entry copied no id and
 * folder sharing was unreachable. Do not re-declare a listing entry anywhere
 * without it.
 */

export type {
  ChildEntry,
  ChildrenListResponse,
  FolderAggregates,
  FolderNode,
  UnregisteredEntry,
} from '../../../types/api';

export interface ListChildrenParams {
  serialNumber?: string;
  startDate?: string;
  endDate?: string;
  includeUntagged?: boolean;
  sortBy?: string;
  sortOrder?: string;
  limit?: number;
  offset?: number;
}

export const filesChildrenApi = {
  /**
   * One folder's children. `nodeId: null` asks for the caller's OWN root.
   *
   * The `root` segment is a literal the server routes ahead of `{object_id}`,
   * and it exists so a cold-start browser needs one round trip rather than two:
   * the response carries the real root id in `node`, so the client learns the id
   * and the listing together. There is deliberately no separate resolver — one
   * that had to *create* the root would be a side-effecting GET.
   */
  async listChildren(
    nodeId: string | null,
    params: ListChildrenParams = {}
  ): Promise<ChildrenListResponse> {
    const query = new URLSearchParams();
    if (params.serialNumber && params.serialNumber !== 'All') {
      query.append('serial_number', params.serialNumber);
    }
    if (params.startDate) query.append('start_date', params.startDate);
    if (params.endDate) query.append('end_date', params.endDate);
    if (params.includeUntagged) query.append('include_untagged', 'true');
    if (params.sortBy) query.append('sort_by', params.sortBy);
    if (params.sortOrder) query.append('sort_order', params.sortOrder);
    // Sent only when set, so the page size stays the server's to choose and the
    // query key does not carry a number the caller never picked.
    if (params.limit != null) query.append('limit', String(params.limit));
    if (params.offset != null) query.append('offset', String(params.offset));

    // Encoded even though a node id is a uuid and `root` is a literal: the
    // value reaches here from React state, and a URL segment interpolated raw is
    // the kind of thing that is safe until the day something else can set it.
    const segment = nodeId ? encodeURIComponent(nodeId) : 'root';
    const queryString = query.toString();
    const response = await api.get<ChildrenListResponse>(
      `/api/file-objects/${segment}/children${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  },
};
